// Clients B2C — pure logic of the customer list (Clients.js) and the
// customer page (ClientDetails.js). No React, no fetch. Shapes: Backend
// app/api/client.py (ClientResponse, ClientTicketResponse, UpdateClientRequest,
// GET /{id}/voice) and app/api/commerce.py (GET /orders).
import { TERMINAL_READ_STATUS_VALUES } from '../constants/ticketStatuses';
import { parseUtc } from '../utils/dates';

// Where a customer record came from (clients.source). French defaults of
// clientsPage.source* (existing keys) and clientsUi.sources.*.
export const SOURCE_LABELS = {
  WEB_FORM: { key: 'clientsPage.sourceWebForm', fr: 'Formulaire web' },
  OUTLOOK: { key: 'clientsPage.sourceOutlook', fr: 'Outlook' },
  TICKET_SYNC: { key: 'clientsPage.sourceTicketSync', fr: 'Synchro tickets' },
  GMAIL: { key: 'clientsPage.sourceGmail', fr: 'Gmail' },
  MANUAL: { key: 'clientsPage.sourceManual', fr: 'Manuel' },
  VOICE: { key: 'clientsUi.sources.voice', fr: 'Appel vocal' },
  WHATSAPP: { key: 'clientsUi.sources.whatsapp', fr: 'WhatsApp' },
};

export const sourceLabel = (t, source) => {
  if (!source) return null;
  const entry = SOURCE_LABELS[String(source).toUpperCase()];
  return entry ? t(entry.key, entry.fr) : String(source).toLowerCase().replace(/_/g, ' ');
};

export const addressText = (address) => {
  if (!address) return '';
  if (typeof address === 'string') return address;
  if (address.formatted) return address.formatted;
  const locality = [address.postal_code, address.city].filter(Boolean).join(' ');
  return [address.line1, locality, address.country].filter(Boolean).join(', ');
};

export const addressNeedsReview = (client) => client?.address_status === 'needs_review';

// List filters: ?vip=1 (historical link) and ?filter=address. Both apply to
// the loaded page only — the API has neither filter.
export const B2C_FILTERS = ['all', 'vip', 'address'];

export const matchesB2CFilter = (client, filter) => {
  if (filter === 'vip') return Boolean(client.is_vip);
  if (filter === 'address') return addressNeedsReview(client);
  return true;
};

// Numbers of the loaded page (GET /api/clients has no total: every count
// says « affichés »).
export const b2cPageSummary = (clients) => {
  const list = clients || [];
  return {
    shown: list.length,
    vip: list.filter((client) => client.is_vip).length,
    withPhone: list.filter((client) => client.phone_number).length,
    withEmail: list.filter((client) => client.email).length,
    needsReview: list.filter(addressNeedsReview).length,
  };
};

// The edit form of a customer: one table for the modal (labels are the
// French defaults of clientsUi.b2cFields.<key>).
export const B2C_FIELDS = [
  { key: 'name', type: 'text', label: 'Nom', placeholder: 'Prénom Nom', autoComplete: 'name' },
  { key: 'email', type: 'email', label: 'E-mail', placeholder: 'nom@exemple.fr', autoComplete: 'email' },
  { key: 'phone_number', type: 'tel', label: 'Téléphone', placeholder: '+33 6 12 34 56 78', autoComplete: 'tel' },
  { key: 'address_line', type: 'text', label: 'Adresse (numéro et rue)', placeholder: '3 rue des Lilas', autoComplete: 'address-line1', full: true },
  { key: 'postal_code', type: 'text', label: 'Code postal', placeholder: '69003', autoComplete: 'postal-code' },
  { key: 'city', type: 'text', label: 'Ville', placeholder: 'Lyon', autoComplete: 'address-level2' },
  { key: 'country', type: 'text', label: 'Pays', placeholder: 'France', autoComplete: 'country-name' },
];

export const formFromCustomer = (client) => ({
  name: client?.name || '',
  email: client?.email || '',
  phone_number: client?.phone_number || '',
  address_line: client?.address?.line1 || '',
  postal_code: client?.address?.postal_code || '',
  city: client?.address?.city || '',
  country: client?.address?.country || '',
});

// PATCH /api/clients/{id} (UpdateClientRequest) — the shape the page always sent.
export const buildCustomerUpdatePayload = (form) => ({
  name: form.name,
  email: form.email,
  phone_number: form.phone_number,
  address_line: form.address_line,
  postal_code: form.postal_code,
  city: form.city,
  country: form.country,
});

const isOpen = (ticket) => ticket.status && !TERMINAL_READ_STATUS_VALUES.includes(ticket.status);

// Tickets of the loaded page: how many still open, how many closed by the AI.
export const customerTicketSummary = (tickets) => {
  const list = tickets || [];
  return {
    shown: list.length,
    open: list.filter(isOpen).length,
    resolved: list.filter((ticket) => TERMINAL_READ_STATUS_VALUES.includes(ticket.status)).length,
    byAi: list.filter((ticket) => ticket.resolution?.by === 'ai' || ticket.status === 'ai_resolved').length,
    lastActivity: list.reduce((latest, ticket) => {
      const date = parseUtc(ticket.updated_at);
      return date && (!latest || date > latest) ? date : latest;
    }, null),
  };
};

// Orders of the customer (GET /api/commerce/orders?q=<email>).
export const customerOrderSummary = (ordersAnswer) => {
  if (!ordersAnswer) return null;
  const items = ordersAnswer.items || [];
  const currencies = [...new Set(items.map((order) => order.currency).filter(Boolean))];
  const amount = currencies.length === 1
    ? items.reduce((sum, order) => sum + (Number(order.total) || 0), 0)
    : null;
  return {
    total: Number.isFinite(Number(ordersAnswer.total)) ? Number(ordersAnswer.total) : items.length,
    shown: items.length,
    amount,
    currency: currencies.length === 1 ? currencies[0] : null,
    problems: items.filter((order) => order.problem_open),
    lastPlaced: items.reduce((latest, order) => {
      const date = parseUtc(order.placed_at);
      return date && (!latest || date > latest) ? date : latest;
    }, null),
  };
};

// What needs a person on this customer.
export const customerAttention = ({ client, orders }) => {
  const items = [];
  if (addressNeedsReview(client)) items.push({ key: 'address', tone: 'warning' });
  (orders?.items || []).filter((order) => order.problem_open).forEach((order) => {
    items.push({ key: `order-${order.id}`, kind: 'order', tone: 'danger', order });
  });
  return items.map((item) => ({ kind: item.kind || item.key, ...item }));
};

// One chronological feed (newest first) from what the page loaded.
export const customerHistory = ({ client, tickets, orders, calls }) => {
  const events = [];
  (tickets || []).forEach((ticket) => {
    if (ticket.created_at) events.push({ key: `t-${ticket.id}`, kind: 'ticket', at: ticket.created_at, ticket });
    if (ticket.resolution?.at) events.push({ key: `r-${ticket.id}`, kind: 'resolved', at: ticket.resolution.at, ticket });
  });
  (orders || []).forEach((order) => {
    if (order.placed_at) events.push({ key: `o-${order.id}`, kind: 'order', at: order.placed_at, order });
  });
  (calls || []).forEach((call) => {
    if (call.started_at) events.push({ key: `c-${call.call_id}`, kind: 'call', at: call.started_at, call });
  });
  if (client?.created_at) events.push({ key: 'created', kind: 'created', at: client.created_at });
  return events
    .map((event) => ({ ...event, time: parseUtc(event.at)?.getTime() || 0 }))
    .sort((a, b) => b.time - a.time);
};
